<script setup lang="ts">
import { runCommand } from '@/commands/registry'
import { ref, watch } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useModal } from '@/composables/useModalStack'
import { useI18n } from '@/i18n'
import { MAX_DOCUMENT_DIMENSION } from '@/types/document'
import type { Interpolation } from '@/pixels/transform'

const emit = defineEmits<{ close: [] }>()
useModal('image-size')
const { t } = useI18n()
const docStore = useDocumentStore()
const cur = docStore.doc!.meta
const width = ref(cur.width)
const height = ref(cur.height)
const keepRatio = ref(true)
const method = ref<Interpolation>('bicubic')
const ratio = cur.width / cur.height
let lock = false
watch(width, v => {
  if (lock || !keepRatio.value) return
  lock = true
  height.value = Math.max(1, Math.round(v / ratio))
  lock = false
})
watch(height, v => {
  if (lock || !keepRatio.value) return
  lock = true
  width.value = Math.max(1, Math.round(v * ratio))
  lock = false
})
function run() {
  if (!(width.value >= 1 && height.value >= 1 && width.value <= MAX_DOCUMENT_DIMENSION && height.value <= MAX_DOCUMENT_DIMENSION)) return
  void runCommand('image.resize', { width: width.value, height: height.value, method: method.value })
  emit('close')
}
</script>

<template>
  <div class="modal-backdrop" @pointerdown.self="emit('close')">
    <div class="modal" @keydown.esc="emit('close')" @keydown.enter="run">
      <h2>{{ t('dialog.image.title') }}</h2>
      <div class="field"><span>{{ t('dialog.canvas.current') }}</span><span class="mono">{{ cur.width }} × {{ cur.height }} px</span></div>
      <label class="field"><span>{{ t('dialog.new.width') }}</span><span class="row"><input v-model.number="width" type="number" min="1" :max="MAX_DOCUMENT_DIMENSION" autofocus /> px</span></label>
      <label class="field"><span>{{ t('dialog.new.height') }}</span><span class="row"><input v-model.number="height" type="number" min="1" :max="MAX_DOCUMENT_DIMENSION" /> px</span></label>
      <label class="field"><span /><span class="row"><input v-model="keepRatio" type="checkbox" /> {{ t('dialog.image.keepRatio') }}</span></label>
      <label class="field"><span>{{ t('dialog.image.method') }}</span>
        <select v-model="method">
          <option value="bicubic">{{ t('dialog.image.bicubic') }}</option>
          <option value="bilinear">{{ t('dialog.image.bilinear') }}</option>
          <option value="nearest">{{ t('dialog.image.nearest') }}</option>
        </select>
      </label>
      <div class="actions">
        <button @click="emit('close')">{{ t('dialog.cancel') }}</button>
        <button class="primary" @click="run">{{ t('dialog.ok') }}</button>
      </div>
    </div>
  </div>
</template>
